<script setup lang="ts">
import { XIcon, XRadio, XCheckbox } from '@clicksign/excalibur';
import { useI18n } from '#i18n';

import { BrandButton } from '~~base-ui';
import { Decisions, useFeature } from '~~core';

type DocumentSelectorEmit = {
  (event: 'selected', value: string): void;
};

type DocumentSelectorProps = {
  value?: string,
  loading?: boolean
  documentscopy?: boolean
  disabledDocuments?: Set<string>,
};

const props = withDefaults(defineProps<DocumentSelectorProps>(), {
  value: '',
  loading: false,
  documentscopy: false,
});

const { t } = useI18n();

const emit = defineEmits<DocumentSelectorEmit>();

const { value } = toRefs(props);
const selectedType = ref(props.value);
const hasIAgree = ref(false);

const { isEnabled: isOcrSyncEnabled } = useFeature(Decisions.OCR_SYNC);

const isDisabledType = (name: string) => {
  // OCR_SYNC
  // TODO: when toggle is removed remove the isOcrSyncEnabled check
  if (!isOcrSyncEnabled.value) return false;

  return props.disabledDocuments?.has(name);
};

const documents = computed(() => [
  {
    value: t('requirement.facialBiometrics.documentType.officialDocuments.cnh.title'),
    description: t('requirement.facialBiometrics.documentType.officialDocuments.cnh.text'),
    icon: 'id-card',
    iconStyle: 'fal',
    name: 'CNH',
    isDisabled: false,
  },
  {
    value: t('requirement.facialBiometrics.documentType.officialDocuments.rg.title'),
    description: t('requirement.facialBiometrics.documentType.officialDocuments.rg.text'),
    icon: 'id-doc-front',
    iconStyle: 'fak',
    name: 'RG',
    isDisabled: false,
  },
].map((type) => ({ ...type, isDisabled: isDisabledType(type.name) })));

watchEffect(() => {
  // OCR_SYNC
  // TODO: when toggle is removed remove the isOcrSyncEnabled check
  if (!isOcrSyncEnabled.value) return;

  if (!props.disabledDocuments?.has(toValue(value))) {
    selectedType.value = toValue(value);
  } else {
    selectedType.value = '';
  }
});

const isButtonDisabled = computed(() => !hasIAgree.value || !selectedType.value);
</script>

<template>
  <section :class="$style.documentTypeContainer">
    <h4 :class="$style.title">
      {{ t('requirement.facialBiometrics.documentType.title') }}
    </h4>
    <p :class="$style.instruction">
      {{ t('requirement.facialBiometrics.documentType.subtitle') }}
    </p>

    <div :class="$style.content">
      <div
        v-for="document in documents"
        :key="`type-${document.value}`"
        :class="[
          $style.radioContainer,
          selectedType === document.value && $style.active,
          document.isDisabled && $style.disabled,
        ]"
      >
        <div :class="$style.radioText">
          <XRadio
            v-model="selectedType"
            :value="document.value"
            :checked="selectedType === document.value"
            name="documentType"
            :data-testid="`documentTypeRadio-${document.value}`"
            :disabled="document.isDisabled"
          />
          <div :class="$style.text">
            <span> {{ document.value }} </span>
            <small :class="$style.subtitle">
              {{ document.description }}
            </small>
          </div>
        </div>
        <div :class="$style.iconContainer">
          <XIcon
            :icon="document.icon"
            :icon-style="document.iconStyle"
            :class="$style.icon"
            size="lg"
            inverse
          />
        </div>
      </div>
    </div>
    <XCheckbox
      v-model="hasIAgree"
      :class="$style.iAgree"
      data-testid="facialBiometricIAgreeCheckbox"
    >
      {{ documentscopy ? t('requirement.documentscopy.iAgreeTo') : t('requirement.facialBiometrics.documentType.iAgreeTo') }}
    </XCheckbox>
    <BrandButton
      full-width
      :disabled="isButtonDisabled"
      :loading="loading"
      data-testid="documentSelectorButton"
      @click="emit('selected', selectedType)"
    >
      {{ t('requirement.common.takePhoto') }}
    </BrandButton>
  </section>
</template>

<style lang="scss" module>
  .documentTypeContainer {
    display: flex;
    align-items: center;
    flex-direction: column;
    width: 100%;
    max-width: 498px;
    padding: var(--space-regular) 0;
    color: var(--color-neutral-1000);

    .title {
      margin: 0;
      font-size: var(--font-size-large);
      font-weight: var(--font-weight-bold);
      text-align: center;
      line-height: var(--line-height-regular);

      @media (max-width: 576px) {
        margin-bottom: var(--space-medium);
      }
    }

    .instruction {
      margin-bottom: var(--space-regular);
      font-size: var(--font-size-regular);
      font-weight: var(--font-weight-regular);
      text-align: center;
    }

    .iAgree {
      width: 100%;
      max-width: 400px;
      margin-bottom: var(--space-medium);
      font-size: var(--font-size-regular);
    }

    .iconContainer {
      --size: 42px;

      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--size);
      height: var(--size);
      background: var(--color-support-04-100);
      border-radius: 50%;
    }

    .disabled .iconContainer {
      background: var(--color-neutral-0);
    }

    .icon {
      color: var(--color-brand-primary-400);
    }

    .content {
      width: 100%;
      max-width: 400px;
    }

    .radioContainer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: var(--space-medium);
    padding: var(--space-small-x) var(--space-medium);
    border: var(--border-width-medium) solid var(--color-brand-support-01-100);
    border-radius: var(--border-radius-medium);
    cursor: pointer;

    &.disabled {
      .text, .subtitle {
        color: var(--color-neutral-500);
      }
    }

    .text {
      display: flex;
      flex-direction: column;
      gap: var(--space-small-xxx);
    }

    .subtitle {
      color: var(--color-neutral-700);
    }

    .radioText {
      display: flex;
      align-items: center;
      gap: var(--space-regular);
    }
  }

  .active {
    border-color: var(--color-brand-primary-400);
  }

  .disabled {
    cursor: not-allowed;

    .icon {
      color: var(--color-neutral-500);
    }
  }
}
</style>
